class CollectionCollaborationsCarousel extends HTMLElement {
  #abortController = new AbortController();
  #pointerStart = null;
  #suppressClick = false;

  connectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();

    const signal = this.#abortController.signal;
    const track = this.querySelector('.collection-collaborations__panels');

    if (!track) return;

    this.#setActivePanel(track.firstElementChild);

    track.addEventListener('click', this.#handleClick, { signal });
    track.addEventListener('keydown', this.#handleKeydown, { signal });
    track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    track.addEventListener('pointerup', this.#handlePointerUp, { signal });
    track.addEventListener('pointercancel', this.#clearPointer, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = event.target.closest('.collection-collaborations__link');
    const panel = link?.closest('.collection-collaborations__panel');

    if (this.#suppressClick) {
      event.preventDefault();
      this.#suppressClick = false;
      return;
    }

    if (!link || !panel || panel.classList.contains('is-active')) return;

    event.preventDefault();
    this.#rotateTo(panel);
  };

  #handleKeydown = (event) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) return;

    event.preventDefault();

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      this.#showPrevious();
    } else {
      this.#showNext();
    }

    this.querySelector('.collection-collaborations__panel.is-active .collection-collaborations__link')?.focus();
  };

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  };

  #handlePointerUp = (event) => {
    if (!this.#pointerStart) return;

    const deltaX = event.clientX - this.#pointerStart.x;
    const deltaY = event.clientY - this.#pointerStart.y;
    const isMobile = matchMedia('(max-width: 749px)').matches;
    const delta = isMobile ? deltaY : deltaX;

    this.#pointerStart = null;

    if (Math.abs(delta) < 50) return;

    this.#suppressClick = true;
    window.setTimeout(() => {
      this.#suppressClick = false;
    }, 0);

    if (delta < 0) {
      this.#showNext();
    } else {
      this.#showPrevious();
    }
  };

  #clearPointer = () => {
    this.#pointerStart = null;
  };

  #showNext() {
    const track = this.querySelector('.collection-collaborations__panels');
    const nextPanel = track?.firstElementChild?.nextElementSibling;

    if (nextPanel) this.#rotateTo(nextPanel);
  }

  #showPrevious() {
    const track = this.querySelector('.collection-collaborations__panels');
    const previousPanel = track?.lastElementChild;

    if (!track || !previousPanel || previousPanel === track.firstElementChild) return;

    track.insertBefore(previousPanel, track.firstElementChild);
    this.#setActivePanel(previousPanel);
  }

  #rotateTo(panel) {
    const track = panel.parentElement;

    if (!track) return;

    while (track.firstElementChild && track.firstElementChild !== panel) {
      track.appendChild(track.firstElementChild);
    }

    this.#setActivePanel(panel);
  }

  #setActivePanel(activePanel) {
    if (!activePanel) return;

    this.querySelectorAll('.collection-collaborations__panel').forEach((panel, index) => {
      const isActive = panel === activePanel;
      panel.style.setProperty('--position', index);
      panel.classList.toggle('is-active', isActive);
      panel.querySelector('.collection-collaborations__link')?.setAttribute('aria-current', String(isActive));
    });
  }
}

if (!customElements.get('collection-collaborations-carousel')) {
  customElements.define('collection-collaborations-carousel', CollectionCollaborationsCarousel);
}
